import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Select, Switch, message, Row, Col, Card, Button, Divider, Alert, Space } from "antd";
import { DatabaseOutlined, FileTextOutlined, BookOutlined } from "@ant-design/icons";
import { scenarioContextEnhancedService } from "../../../../api/enhancedApi";
import SupplierSelector from "../../../../components/common/SupplierSelector";
import { STANDARD_COST_TYPE_OPTIONS } from "../../../settlement/constants";
import ProductListEditor from "./ProductListEditor";
import { normalizeScenarioContextAgentOptions } from "../constants";

const { TextArea } = Input;
const { Option } = Select;

/**
 * 知识领域选项 —— 决定同一 KNOWLEDGE 类型下具体存什么内容
 *
 * 新增领域时只需在此追加一项，表单会自动生成对应的编辑区域。
 */
const KNOWLEDGE_DOMAIN_OPTIONS = [
    { value: "ap_products", label: "产品知识库", description: "产品识别列表" },
    { value: "ap_billing_rules", label: "计费规则", description: "定价/计算规则" },
];

const KNOWLEDGE_DOMAIN_LABELS = Object.fromEntries(KNOWLEDGE_DOMAIN_OPTIONS.map((o) => [o.value, o.label]));

const validateProductList = (_, value) => {
    const products = value?.products || [];
    if (products.length === 0) {
        return Promise.reject(new Error("请至少添加一个产品"));
    }

    const seenCodes = new Set();
    for (const product of products) {
        const productCode = String(product.product_code || "").trim();
        if (!productCode || !String(product.name || "").trim() || !product.cost_type || !product.unit || !String(product.recognition_hints || "").trim()) {
            return Promise.reject(new Error("请补全产品代码、产品名称、费用类型、单位和识别提示"));
        }
        if (seenCodes.has(productCode)) {
            return Promise.reject(new Error(`产品代码 "${productCode}" 重复`));
        }
        seenCodes.add(productCode);
        const airportCode = String(product.airport_iata_code || "").trim().toUpperCase();
        if (airportCode && !/^[A-Z]{3}$/.test(airportCode)) {
            return Promise.reject(new Error(`机场代码 "${airportCode}" 不是 3 位 IATA 代码`));
        }
    }

    return Promise.resolve();
};

const ScenarioContextForm = ({ visible, mode, initialValues, onCancel, onSuccess }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [agentOptions, setAgentOptions] = useState([]);
    const [entryType, setEntryType] = useState("PROMPT");
    const [knowledgeDomain, setKnowledgeDomain] = useState(null);

    // 加载Agent名称列表
    useEffect(() => {
        const loadAgentNames = async () => {
            try {
                const response = await scenarioContextEnhancedService.getAgentNames();
                setAgentOptions(normalizeScenarioContextAgentOptions(response.data.agent_options || response.data.items || []));
            } catch (error) {
                message.error("加载Agent名称失败");
            }
        };

        if (visible) {
            loadAgentNames();
        }
    }, [visible]);

    // 表单提交
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            // ── 组装 scenario_conditions ──
            const scenarioConditions = {};
            if (values.supplier_id) {
                scenarioConditions.supplier_id = String(values.supplier_id);
            }
            if (values.standard_cost_type) {
                scenarioConditions.standard_cost_type = values.standard_cost_type;
            }
            if (values.knowledge_domain) {
                scenarioConditions.knowledge_domain = values.knowledge_domain;
            }
            if (values.strategy_label) {
                scenarioConditions.strategy_label = values.strategy_label.trim();
            }

            const submitData = {
                agent_name: values.agent_name,
                entry_type: values.entry_type,
                scenario_conditions: Object.keys(scenarioConditions).length > 0 ? scenarioConditions : null,
                description: values.description,
                is_active: values.is_active,
            };

            // ── 根据类型组装内容 ──
            if (values.entry_type === "KNOWLEDGE") {
                if (values.knowledge_domain === "ap_billing_rules") {
                    submitData.json_content = {
                        rules_text: values.rules_text || "",
                        metadata: values.rules_metadata || {},
                    };
                } else {
                    submitData.json_content = values.json_content;
                }
                submitData.prompt_text = null;
            } else {
                submitData.prompt_text = values.prompt_text;
                submitData.json_content = null;
            }

            if (mode === "add") {
                const createResponse = await scenarioContextEnhancedService.create(submitData);
                if (createResponse.data.success) {
                    message.success(createResponse.data.message || "创建成功");
                    onSuccess();
                } else {
                    throw new Error(createResponse.data.message || "创建失败");
                }
            } else {
                const updateResponse = await scenarioContextEnhancedService.update({ id: initialValues.id, data: submitData });
                if (updateResponse.data.success) {
                    message.success(updateResponse.data.message || "更新成功");
                    onSuccess();
                } else {
                    throw new Error(updateResponse.data.message || "更新失败");
                }
            }
        } catch (error) {
            console.error("提交失败:", error);
            message.error(error.response?.data?.detail || "提交失败");
        } finally {
            setLoading(false);
        }
    };

    // 重置表单
    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    // 初始化表单数据
    useEffect(() => {
        if (visible && initialValues) {
            const conditions = initialValues.scenario_conditions || {};
            const currentEntryType = initialValues.entry_type || "PROMPT";
            const domain = conditions.knowledge_domain || null;

            setEntryType(currentEntryType);
            setKnowledgeDomain(domain);

            // supplier_id 现在是 UUID 字符串，直接使用
            let supplierId = conditions.supplier_id || undefined;

            const formValues = {
                agent_name: initialValues.agent_name,
                entry_type: currentEntryType,
                supplier_id: supplierId || undefined,
                standard_cost_type: conditions.standard_cost_type || undefined,
                knowledge_domain: domain || undefined,
                strategy_label: conditions.strategy_label || undefined,
                prompt_text: initialValues.prompt_text,
                json_content: initialValues.json_content || { products: [] },
                description: initialValues.description,
                is_active: initialValues.is_active,
            };

            // 计费规则：提取 rules_text 到独立字段
            if (domain === "ap_billing_rules" && initialValues.json_content) {
                formValues.rules_text = initialValues.json_content.rules_text || "";
                formValues.rules_metadata = initialValues.json_content.metadata || {};
            }

            form.setFieldsValue(formValues);
        } else if (visible) {
            setEntryType("PROMPT");
            setKnowledgeDomain(null);
            form.setFieldsValue({
                entry_type: "PROMPT",
                is_active: true,
                json_content: { products: [] },
                strategy_label: undefined,
            });
        }
    }, [visible, initialValues, form]);

    return (
        <Modal
            title={mode === "add" ? "创建场景化上下文" : "编辑场景化上下文"}
            open={visible}
            onCancel={handleCancel}
            width={entryType === "KNOWLEDGE" ? 1100 : 900}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    取消
                </Button>,
                <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
                    {mode === "add" ? "创建" : "更新"}
                </Button>,
            ]}
            className="scenario-prompt-form-modal fullscreen-modal"
            styles={{ body: { maxHeight: "none", overflowY: "visible" } }}
        >
            <Form form={form} layout="vertical">
                {/* ════════ 基础信息 ════════ */}
                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item name="agent_name" label="Agent名称" rules={[{ required: true, message: "请选择Agent名称" }]}>
                            <Select placeholder="请选择Agent名称" allowClear showSearch optionFilterProp="label" options={agentOptions} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="entry_type" label="类型" rules={[{ required: true, message: "请选择类型" }]}>
                            <Select placeholder="请选择类型" onChange={(value) => setEntryType(value)}>
                                <Option value="PROMPT">
                                    <Space>
                                        <FileTextOutlined />
                                        提示词
                                    </Space>
                                </Option>
                                <Option value="KNOWLEDGE">
                                    <Space>
                                        <DatabaseOutlined />
                                        知识库
                                    </Space>
                                </Option>
                                <Option value="CONTEXT">
                                    <Space>
                                        <FileTextOutlined />
                                        上下文
                                    </Space>
                                </Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="is_active" label="启用状态" valuePropName="checked">
                            <Switch checkedChildren="启用" unCheckedChildren="停用" />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item name="description" label="描述">
                    <Input placeholder="请输入描述信息（可选）" />
                </Form.Item>

                {/* ════════ 场景匹配条件（固定字段） ════════ */}
                <Card
                    size="small"
                    title="场景匹配条件"
                    style={{ marginBottom: 24, background: "#fafafa" }}
                >
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="supplier_id" label="供应商">
                                <SupplierSelector placeholder="请选择供应商" allowClear style={{ width: "100%" }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="standard_cost_type" label="标准费用类型">
                                <Select
                                    placeholder="请选择费用类型"
                                    options={STANDARD_COST_TYPE_OPTIONS}
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="knowledge_domain" label="知识领域">
                                <Select
                                    placeholder="请选择知识领域"
                                    allowClear
                                    onChange={(val) => setKnowledgeDomain(val)}
                                >
                                    {KNOWLEDGE_DOMAIN_OPTIONS.map((opt) => (
                                        <Option key={opt.value} value={opt.value}>
                                            <Space>
                                                <BookOutlined />
                                                {opt.label}
                                            </Space>
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="strategy_label" label="策略标签">
                                <Input placeholder="如：综合平衡策略 / 最大载运量" allowClear />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                <Divider orientation="left">内容配置</Divider>

                {/* ════════ 内容编辑器（按 entry_type + knowledge_domain 切换） ════════ */}
                {entryType === "KNOWLEDGE" ? (
                    knowledgeDomain === "ap_billing_rules" ? (
                        <>
                            <Alert
                                message="计费规则说明"
                                description="计费规则以 Markdown 格式存储，供 Agent 在批次验证时读取。内容应包含费用项定义、计算公式、单价等信息。"
                                type="info"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                            <Form.Item
                                name="rules_text"
                                label="计费规则内容 (Markdown)"
                                rules={[{ required: true, message: "请输入计费规则内容" }]}
                            >
                                <TextArea
                                    rows={16}
                                    placeholder={`# 供应商名称 - 费用规则

## 费用项
- Landing Fee: €5.37/ton (MTOW)
- Parking Fee: €0.50/ton/hour (free ≤ 2h)

## 计算公式
Landing Amount = MTOW × Unit Price
...`}
                                    style={{ minWidth: "600px", fontFamily: "monospace" }}
                                />
                            </Form.Item>
                        </>
                    ) : (
                        <>
                            <Alert
                                message="知识库类型说明"
                                description="知识库用于存储结构化数据（如产品列表），供 Agent 在处理任务时参考。产品数据会在 AI 解析账单时用于识别和匹配产品信息。"
                                type="info"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                            <Form.Item
                                name="json_content"
                                label="产品列表"
                                rules={[{ validator: validateProductList }]}
                            >
                                <ProductListEditor />
                            </Form.Item>
                        </>
                    )
                ) : (
                    <Form.Item
                        name="prompt_text"
                        label={entryType === "PROMPT" ? "提示词内容" : "上下文内容"}
                        rules={[{ required: true, message: entryType === "PROMPT" ? "请输入提示词内容" : "请输入上下文内容" }]}
                    >
                        <TextArea
                            rows={8}
                            placeholder={
                                entryType === "PROMPT"
                                    ? "请输入提示词内容，这是传递给Agent的实际指令..."
                                    : "请输入上下文信息，这是 Agent 运行时的参考数据..."
                            }
                            style={{ minWidth: "600px" }}
                        />
                    </Form.Item>
                )}
            </Form>
        </Modal>
    );
};

export default ScenarioContextForm;
